import * as z from 'zod';

import { text } from '../envelope';
import { defineEvent, NO_CONTENT_IDS } from './define';

export type { EventDefinition, EventFamily } from './define';

/**
 * The reader events, schema version 1. Names are `object_action` and state
 * what the reader did or what happened, not which control was pressed. They
 * are English identifiers, not UI labels, and do not go through `@shared/i18n`.
 *
 * Only events the app emits are defined.
 */
export const catalog = {
	// App
	/** A route is shown. Carries the issue/article ids when the route shows one. */
	screen_view: defineEvent({
		family: 'app',
		params: z.strictObject({
			/** The route pattern, not the URL. */
			screen: text,
			path: text,
		}),
		requires: () => NO_CONTENT_IDS,
	}),
	app_install: defineEvent({
		family: 'app',
		params: z.strictObject({}),
		requires: () => NO_CONTENT_IDS,
	}),
	install_click: defineEvent({
		family: 'app',
		params: z.strictObject({ location: text }),
		requires: () => NO_CONTENT_IDS,
	}),
	install_prompt_result: defineEvent({
		family: 'app',
		params: z.strictObject({ outcome: z.enum(['accepted', 'dismissed']) }),
		requires: () => NO_CONTENT_IDS,
	}),

	// Account and access
	login_start: defineEvent({
		family: 'account',
		params: z.strictObject({ location: text }),
		requires: () => NO_CONTENT_IDS,
	}),
	login: defineEvent({
		family: 'account',
		/** `method` is the tenant's auth type. */
		params: z.strictObject({ method: text }),
		requires: () => NO_CONTENT_IDS,
	}),

	/**
	 * A gate stopped the reader. Recorded for every tenant, whether or not
	 * it has a paywall, so the funnel `paywall_hit` → `login_start` →
	 * `login` / `subscribe_click` can be read within a session.
	 */
	paywall_hit: defineEvent({
		family: 'account',
		params: z.strictObject({
			gate: z.enum(['route', 'issue', 'article']),
			reason: z.enum(['login_required', 'subscription_required', 'expired']),
		}),
		requires: ({ gate }) =>
			gate === 'article'
				? ['issue_id', 'article_id']
				: gate === 'issue'
					? ['issue_id']
					: NO_CONTENT_IDS,
	}),
	/** The reader followed the tenant's subscription link. */
	subscribe_click: defineEvent({
		family: 'account',
		params: z.strictObject({ location: text }),
		requires: () => NO_CONTENT_IDS,
	}),

	// Reading
	/** An issue's reader opened. */
	issue_open: defineEvent({
		family: 'reading',
		params: z.strictObject({
			source: z.enum(['home', 'deeplink', 'favorite', 'downloads', 'other']),
		}),
		requires: () => ['issue_id'],
	}),
	/** An article became the active one (responsive layout). */
	article_open: defineEvent({
		family: 'reading',
		params: z.strictObject({
			source: z.enum(['toc', 'swipe', 'deeplink', 'favorite', 'other']),
			/**
			 * How many articles the issue has, as this device saw it. With
			 * `page_number` it says how far through the issue the reader is.
			 */
			units_total: z.number().int().positive().optional(),
		}),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/**
	 * The reader left an article: moved on, hid the tab or closed the page.
	 * It fires each time, so one reader can have several rows for one
	 * article; the reading side sums `dwell_ms` and takes the highest depth.
	 */
	article_read: defineEvent({
		family: 'reading',
		params: z.strictObject({
			/** Engaged time (`engaged-clock.ts`), whole milliseconds. */
			dwell_ms: z.number().int().nonnegative(),
			/** 0 to 1, two decimals. */
			max_scroll_depth: z.number().min(0).max(1),
			exit: z.enum(['next', 'back', 'hidden', 'closed']),
		}),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/**
	 * The first time on this device that the article met the completion
	 * rule: read to at least 90% of its depth, with at least 10 seconds of
	 * engaged time, across however many visits that took (`thresholds.ts`).
	 */
	article_complete: defineEvent({
		family: 'reading',
		/** Engaged time on the article so far, whole milliseconds. */
		params: z.strictObject({ dwell_ms: z.number().int().nonnegative() }),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/**
	 * The first time on this device that the issue met the completion rule:
	 * 90% of its pages viewed (replica) or 80% of its articles opened
	 * (responsive). The units are pages or articles accordingly, and
	 * `units_total` is the issue's size as the device saw it.
	 */
	issue_complete: defineEvent({
		family: 'reading',
		params: z.strictObject({
			units_seen: z.number().int().nonnegative(),
			units_total: z.number().int().positive(),
		}),
		requires: () => ['issue_id'],
	}),
	/** A replica page was shown. One event per visible page. */
	issue_page_view: defineEvent({
		family: 'reading',
		params: z.strictObject({
			pages_visible: z.union([z.literal(1), z.literal(2)]),
			/** How many pages the issue has, as this device saw it. */
			units_total: z.number().int().positive().optional(),
		}),
		requires: () => ['issue_id', 'page_number'],
	}),
	/** The reader left a replica page. One event per visible page. */
	issue_page_leave: defineEvent({
		family: 'reading',
		params: z.strictObject({
			/** Engaged time the page was on screen, whole milliseconds. */
			dwell_ms: z.number().int().nonnegative(),
			pages_visible: z.union([z.literal(1), z.literal(2)]),
		}),
		requires: () => ['issue_id', 'page_number'],
	}),
	layout_change: defineEvent({
		family: 'reading',
		params: z.strictObject({ to: z.enum(['replica', 'responsive']) }),
		requires: () => ['issue_id'],
	}),
	language_change: defineEvent({
		family: 'reading',
		params: z.strictObject({ to: text, scope: z.enum(['app', 'issue']) }),
		requires: ({ scope }) => (scope === 'issue' ? ['issue_id'] : NO_CONTENT_IDS),
	}),

	// Engagement
	share: defineEvent({
		family: 'engagement',
		params: z.strictObject({
			method: z.enum(['clipboard', 'os']),
			shared_type: z.enum(['issue', 'article']),
		}),
		requires: ({ shared_type }) =>
			shared_type === 'article' ? ['issue_id', 'article_id'] : ['issue_id'],
	}),
	/** An issue finished downloading for offline reading. */
	issue_download: defineEvent({
		family: 'engagement',
		params: z.strictObject({}),
		requires: () => ['issue_id'],
	}),

	// Audio. The ids are those of the article being played, which is not
	// always the one on screen: playback continues while the reader moves on.
	/** Playback started or resumed. */
	audio_play: defineEvent({
		family: 'audio',
		params: z.strictObject({ position_ms: z.number().int().nonnegative() }),
		requires: () => ['issue_id', 'article_id'],
	}),
	/** Playback paused or stopped before the end. */
	audio_pause: defineEvent({
		family: 'audio',
		params: z.strictObject({
			/** Time actually played since the last `audio_play`, whole milliseconds. */
			listened_ms: z.number().int().nonnegative(),
			position_ms: z.number().int().nonnegative(),
		}),
		requires: () => ['issue_id', 'article_id'],
	}),
	/** Playback reached the end. */
	audio_complete: defineEvent({
		family: 'audio',
		params: z.strictObject({
			listened_ms: z.number().int().nonnegative(),
			duration_ms: z.number().int().nonnegative(),
		}),
		requires: () => ['issue_id', 'article_id'],
	}),

	// Ads. `ad_id` is the advert component's id in the article; `ad_host` is
	// the host of where it links to, never the full URL.
	/** An image advert was at least half visible for a second. Once per article view. */
	ad_impression: defineEvent({
		family: 'ads',
		params: z.strictObject({ ad_id: text, ad_host: text.optional() }),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/** The reader opened an image advert's link. */
	ad_click: defineEvent({
		family: 'ads',
		params: z.strictObject({ ad_id: text, ad_host: text }),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/** A Google ad unit rendered. Not an impression: Google measures those. */
	ad_slot_render: defineEvent({
		family: 'ads',
		params: z.strictObject({ ad_id: text, slot_id: text }),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),

	favorite_add: defineEvent({
		family: 'engagement',
		params: z.strictObject({}),
		requires: () => ['issue_id', 'article_id'],
	}),
};

export type EventName = keyof typeof catalog;
export type EventParams<N extends EventName> = z.input<
	(typeof catalog)[N]['params']
>;

export const EVENT_NAMES = Object.keys(catalog) as Array<EventName>;
